<template>
  <div class="mx-auto w-full max-w-7xl">
    <!-- Steps header -->
    <base-card class="p-3 lg:mt-2">
      <a-steps :current="current" class="mb-8">
        <a-step
          v-for="item in steps"
          :key="item.title"
          :title="item.title"
          :description="item.description"
        />
      </a-steps>

      <template v-if="importStep">
        <div class="lg:grid grid-cols-2 grid-flow-col items-center justify-between">
          <div class="lg:mr-12">
            <SingleSelect
              :options="companies_companiesList"
              class="w-full shadow p-4 text-sm"
              v-model="companyId"
            />
          </div>
          <div>
            <!-- Files drop zone -->
            <div class="mt-5 lg:mt-0">
              <div
                class="h-32 lg:h-48 cursor-pointer border-dashed border ease-in-out duration-300 hover:text-primary-light text-primary border-primary hover:border-primary-light"
                @drop="handleFileUpload($event, 'dropped')"
                @dragover.prevent
                @dragenter.prevent
                @click="$refs.fileInput.click()"
              >
                <div class="flex flex-col justify-center items-center bg-secondary-light h-full">
                  <base-icon icon="cloud-upload" class="text-5xl" />
                  <span class="mx-2 text-gray-400">{{ dropZoneText }}</span>
                </div>
              </div>
              <input
                ref="fileInput"
                class="hidden"
                type="file"
                name="fileArray"
                :accept="supportedFiles.text"
                multiple
                @change="handleFileUpload($event, 'selected')"
              />
            </div>
            <p class="test-secondary-dark">
              <small> {{ replaceSupportedFormats }} {{ supportedFiles.text }} </small>
            </p>
          </div>
        </div>
        <hr class="mt-10 mb-4" />
        <div class="steps-content" v-if="toursToImport.length > 0">
          <!-- Uploaded tours list -->
          <div class="grid">
            <div class="hidden sm:grid grid-cols-2">
              <span class="text-secondary-dark">{{
                this.translatedText('mygwf.components.ToursImport.edit_tour_name_optional')
              }}</span>
              <span class="text-secondary-dark"
                >{{ this.translatedText('mygwf.components.ToursImport.assign_to_tablet_optional') }}
              </span>
            </div>
            <div v-for="(tour, index) in toursToImport" :key="index" class="mt-1 py-2">
              <div
                class="sm:grid sm:grid-cols-8 items-center justify-items-center space-y-3 sm:space-y-0"
              >
                <base-input v-model="tour.name" type="text" class="col-span-3" condensed />
                <base-icon
                  class="hidden sm:inline-block transform -rotate-90 w-4 h-4 text-secondary-dark"
                  icon="arrow-down"
                />
                <base-select
                  v-model="tour.tablet_uuid"
                  class="col-span-3"
                  :placeholder="translatedText('mygwf.components.ToursImport.select_a_device')"
                  clearable
                  searchable
                  :options="tablets"
                  :option-type="{id: 'uuid', label: 'name'}"
                />
                <base-button-icon
                  @click="onRemoveTour(index)"
                  size="sm"
                  icon="delete"
                  class="text-red-500"
                />
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="resultStep">
        <div class="flex flex-wrap items-stretch justify-start">
          <div v-for="(tour, index) in toursToImport" :key="index" class="shadow p-4 m-4">
            <div class="font-semibold border-b pb-1 mb-2">{{ tour.name }}</div>
            <div class="flex items-center mb-1 space-x-3">
              <TaskStatus :status="tour.importStatus" />
              <span> {{ translatedText('mygwf.components.ToursImport.upload_file') }} </span>
            </div>
            <div :class="[`flex items-center mb-1 space-x-3`, tour.taskStatus ? `` : `opacity-50`]">
              <TaskStatus :status="tour.taskStatus" />
              <span>{{
                translatedText('mygwf.components.ToursImport.checking_import_status')
              }}</span>
            </div>
            <LogError :errors="tour.log" />
          </div>
        </div>
      </template>
    </base-card>

    <!-- Actions -->
    <div class="steps-action flex justify-end mt-6">
      <div class="w-1/2 flex justify-end space-x-6">
        <base-button
          class="w-1/2"
          action="close"
          context="outline"
          @click="$router.push({path: '/tours'})"
        />
        <base-button
          v-if="current > 0"
          class="w-1/2"
          context="secondary"
          action="previous"
          @click="current--"
        />
        <base-button
          v-if="importStep"
          class="w-1/2"
          action="submit"
          :disabled="toursToImport.length === 0"
          @click="submitForm"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  BaseCard,
  BaseButton,
  BaseIcon,
  BaseInput,
  BaseSelect,
  BaseButtonIcon,
  BaseLabel,
} from '@/components/base'
import SingleSelect from '@/components/shared/SingleSelect.vue'
import TaskStatus from '@/components/shared/TaskStatus.vue'
import LogError from '@/components/shared/LogError.vue'
import {required} from 'vuelidate/lib/validators'
import {mapGetters, mapActions} from 'vuex'
import Steps from 'ant-design-vue/lib/steps'
import 'ant-design-vue/lib/steps/style/index.css'
import _baseLabel from '@/components/base/_base-label'

export default {
  components: {
    'base-label': _baseLabel,
    SingleSelect,
    BaseButton,
    BaseCard,
    BaseIcon,
    BaseInput,
    BaseSelect,
    BaseLabel,
    BaseButtonIcon,
    TaskStatus,
    LogError,
    'a-steps': Steps,
    'a-step': Steps.Step,
  },

  data() {
    return {
      pendingComponentInit: false,
      pendingRequest: false,
      tablets: [],
      toursToImport: [],
      done: undefined,
      current: 0,
      companyId: null,
      steps: [
        {
          title: this.$t('mygwf.components.Form.Step.Import'),
          content: 'Import-content',
        },
        {
          title: this.$t('mygwf.components.Form.Step.Results'),
          content: 'Result-content',
        },
      ],
    }
  },

  computed: {
    //with mapGetters spreaders we can reference the store getters using 'this',i.e 'this.async_tasks'
    ...mapGetters({
      async_tasks: 'asyncTasksTracker/tasks',
      async_getPendingTasks: 'asyncTasksTracker/getPendingTasks',
      tours_supportedImportFormats: 'tours/supportedImportFormats',
      companies_companiesList: 'companies/list',
    }),
    //Steps
    importStep() {
      return this.current === 0
    },
    resultStep() {
      return this.current === 1
    },

    // TODO: Verify DE i18n
    dropZoneText() {
      return this.$t('mygwf.components.Form.dropzone')
    },
    translateStepEdit() {
      return this.$t('mygwf.components.Form.Step.Edit')
    },

    requiredFieldText() {
      return this.$t('mygwf.components.Form.requiredField')
    },

    replaceSupportedFormats() {
      return this.$t('mygwf.components.Form.Supported_Formats')
    },

    supportedFiles() {
      const supportedFormats = this.tours_supportedImportFormats

      let text = ''
      let formats = []
      let types = []
      Object.entries(supportedFormats).forEach(([fileFormat, fileTypes], index) => {
        text += `${index > 0 ? ',' : ''}.${fileFormat}`
        formats.push(fileFormat)
        types.push(...fileTypes.split(','))
      })
      return {text, formats, types}
    },
  },

  created() {
    this.onInitComponent()
  },

  methods: {
    //with mapActions spreaders we can reference the store actions using 'this',i.e 'this.readOuts'
    ...mapActions({
      tours_readOuts: 'tours/readOuts',
      async_addTask: 'asyncTasksTracker/addTask',
      async_updateTaskTitle: 'asyncTasksTracker/updateTaskTitle',
      async_updateTaskStatus: 'asyncTasksTracker/updateTaskStatus',
      async_pendingTasksAmount: 'asyncTasksTracker/pendingTasksAmount',
      tablets_getInitialTablets: 'tablets/getInitialTablets',
      tours_assignTablet: 'tours/assignTablet',
    }),

    translatedText(translationProperty) {
      return this.$t(translationProperty)
    },

    async resetTourState(tour) {
      tour.importStatus = undefined
      tour.taskStatus = undefined
      tour.log = undefined
    },

    async tourImport(tour, {companyId}) {
      let formData = new FormData()
      formData.append('file', tour.file)
      formData.append('name', tour.name)
      formData.append('company', companyId)
      formData.append('overwrite', true)
      if (tour.tablet_uuid != undefined) {
        formData.append('tablet_uuid', tour.tablet_uuid)
      }

      const result = await this.$store.dispatch('tours/importTour', formData, {
        headers: {
          'Content-Type': tour.file?.type,
        },
      })

      return result
    },

    async checkTourStatus({taskId}) {
      let taskResult = await this.tours_readOuts(taskId)
      return taskResult
    },

    async submitForm() {
      this.current++

      const res = await Promise.all(
        this.toursToImport.map(async tour => {
          // Reset in case of re-submission
          await this.resetTourState(tour)

          // Start file upload
          tour.importStatus = 'pending'
          let importResult = undefined
          try {
            importResult = await this.tourImport(tour, {
              companyId: this.companyId,
            })
            tour.importStatus = `success`
          } catch (error) {
            tour.importStatus = 'error'
            tour.log = error
            return 'fail'
          }

          // Upload complete, if it was successful track task
          tour.taskStatus = `pending`
          const taskTrackerId = await this.async_addTask({
            status: 'pending',
            title: `${this.translatedText('mygwf.components.ToursImport.importing_tour_file')}: ${
              tour.name
            }`,
          })

          const taskInterval = setInterval(async () => {
            try {
              let status = await this.checkTourStatus({
                taskId: this.getTaskId(importResult),
              })

              if (status.state === 'FAILURE') {
                throw status
              }

              if (status.state === 'SUCCESS') {
                tour.taskStatus = 'success'
                this.async_updateTaskStatus({
                  id: taskTrackerId,
                  status: 'complete',
                })
                clearInterval(taskInterval)
              }
            } catch (error) {
              tour.taskStatus = 'error'
              tour.log = error
              this.async_updateTaskStatus({
                id: taskTrackerId,
                status: 'failed',
              })
              clearInterval(taskInterval)
              return 'fail'
            }
          }, 5000)

          return 'done'
        })
      )

      this.done = !res.some(res => res === 'fail')
    },

    getTaskId(apiTaskId) {
      // We extract the id portion of the response
      return apiTaskId.split(':')[1].replace(/"/g, '').replace(/ /g, '')
    },

    async assignTablet(tour_uuid, tablet_uuid) {
      //console.log("Assigning tablet...Tour-uuid:"+tour_uuid+" tablet-uuid:"+tablet_uuid)

      let payload = {tablet_uuid: tablet_uuid}
      try {
        let result = await this.tours_assignTablet({tour: {uuid: tour_uuid}, payload})
      } catch (err) {
        console.log(err)
      }
    },

    handleFileUpload(e, action) {
      e.preventDefault()
      const {types: supportedTypes, formats: supportedFormats} = this.supportedFiles
      const files = action === 'dropped' ? e.dataTransfer.files : e.target.files
      const acceptedFiles = []

      for (let i = 0; i < files.length; i++) {
        const fileObject = files[i]
        const fileFormat = fileObject.name.split('.').slice(-1)[0]
        if (
          supportedTypes.includes(fileObject.type) &&
          supportedFormats.includes(fileFormat.toLowerCase())
        ) {
          acceptedFiles.push(fileObject)
        }
        if (acceptedFiles.length === 0) {
          return
        }
        this.toursToImport.push({
          name: fileObject.name,
          importStatus: undefined,
          taskStatus: undefined,
          file: fileObject,
          tablet_uuid: undefined,
        })
      }
    },

    onRemoveTour(index) {
      this.toursToImport.splice(index, 1)
      if (this.toursToImport.length === 0) {
        this.$refs.fileInput.value = ''
      }
    },

    async onInitComponent() {
      try {
        this.pendingComponentInit = true
        let result = await this.tablets_getInitialTablets()
        //console.log("result is:"+result)
        if (result.count > 0) {
          this.tablets = result.results
        }
      } catch (e) {
        console.log(e)
      } finally {
        this.pendingComponentInit = false
      }
    },
  },

  // ================================================================================
  // Validation
  // ================================================================================
  validations: {
    toursToImport: {
      $each: {
        name: {required},
        tabletId: {},
      },
    },
  },
}
</script>

<style>
.anticon.anticon-check.ant-steps-finish-icon svg {
  margin-left: 0.2rem;
  margin-top: -0.5rem;
}
.ant-btn {
  @apply rounded-none;
}
p.ant-upload-drag-icon {
  @apply flex justify-center;
}
.ant-upload-drag-container {
  @apply py-2;
}
.ant-steps-item-process .ant-steps-item-icon {
  @apply bg-primary border-primary;
}
.ant-steps-item-finish .ant-steps-item-icon > .ant-steps-icon {
  @apply text-white;
}
.ant-steps-item-finish .ant-steps-item-icon {
  @apply bg-green-light;
}
.ant-steps-item-finish
  > .ant-steps-item-container
  > .ant-steps-item-content
  > .ant-steps-item-title::after {
  @apply bg-primary;
}
.ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-tail::after {
  @apply bg-primary;
}

.ant-steps-item-error .ant-steps-item-icon {
  @apply bg-red-light;
}

.ant-steps-item-error .ant-steps-item-icon > .ant-steps-icon {
  @apply text-white;
  top: -5px;
}

.ant-steps-item-error
  > .ant-steps-item-container
  > .ant-steps-item-content
  > .ant-steps-item-title {
  @apply text-red-light;
}

input:disabled {
  color: black;
}
</style>
